/**
 * utils/pdfTextSanitizer.ts
 *
 * Sanitizes text strings before passing them to pdf-lib's drawText when using
 * standard (WinAnsi / Windows-1252) fonts such as Helvetica and Helvetica-Bold.
 * pdf-lib throws when it tries to encode a character that has no glyph in the
 * WinAnsi encoding, which would crash the entire PDF generation.
 *
 * sanitizeWinAnsi keeps every character whose Unicode code point maps to a
 * valid Windows-1252 byte and strips everything else.  The character ranges
 * that are safe are:
 *   - U+0020–U+007E  basic printable ASCII
 *   - U+00A0–U+00FF  Latin-1 Supplement (= ISO 8859-1 positions 0xA0–0xFF)
 *   - A small set of higher code points that Windows-1252 maps at positions
 *     0x80–0x9F (€, smart quotes, dashes, Œ/œ, Š/š, Ž/ž, Ÿ, ™, …, •, etc.)
 *
 * Used by bordereauGenerator.ts and pdfDossierBuilder.ts.
 *
 * Created: shared utility to prevent WinAnsi encoding crashes in PDF generation.
 */

const WIN1252_EXTRAS = new Set<number>([
  0x20AC,
  0x201A,
  0x0192,
  0x201E,
  0x2026,
  0x2020,
  0x2021,
  0x02C6,
  0x2030,
  0x0160,
  0x2039,
  0x0152,
  0x017D,
  0x2018,
  0x2019,
  0x201C,
  0x201D,
  0x2022,
  0x2013,
  0x2014,
  0x02DC,
  0x2122,
  0x0161,
  0x203A,
  0x0153,
  0x017E,
  0x0178,
]);

export function sanitizeWinAnsi(text: string): string {
  return Array.from(text)
    .map((char) => {
      const cp = char.codePointAt(0) ?? 0;
      if (cp >= 0x20 && cp <= 0x7E) return char;
      if (cp >= 0xA0 && cp <= 0xFF) return char;
      if (WIN1252_EXTRAS.has(cp)) return char;
      if (cp === 0x09 || cp === 0x0A || cp === 0x0D) return char;
      return '';
    })
    .join('');
}
